import * as React from "react"
import { usePage } from "@inertiajs/react"
import { Menu as MenuIcon } from "lucide-react"
import { toast } from "sonner"
import { RailNav } from "./RailNav"
import { ProjectSwitcher } from "./ProjectSwitcher"
import { HeaderBreadcrumbs } from "./HeaderBreadcrumbs"
import { Toaster } from "@ui/Toaster"
import type { SharedPageProps } from "@lib/inertia-types"
import { cn } from "@lib/cn"

const MOBILE_BREAKPOINT = 1024

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { props } = usePage<SharedPageProps>()
  const auth = props.auth
  const flash = props.flash

  // Rail is open by default on wide screens, collapsed below the breakpoint.
  // matchMedia only fires on breakpoint crossings, so a manual desktop-collapse
  // sticks (a plain resize listener would re-open it on every resize event).
  const [sidebarOpen, setSidebarOpen] = React.useState(true)

  React.useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`)
    const apply = () => setSidebarOpen(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])

  React.useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  if (!auth?.user) {
    return (
      <>
        {children}
        <Toaster />
      </>
    )
  }

  return (
    // grid-rows-[1fr] keeps the single track full height so short pages don't
    // collapse. The teal→blue gradient paints the whole app; the white card
    // floats on top. When the rail is collapsed the grid drops to one column
    // and the card expands to full width.
    <div
      className={cn(
        "grid h-screen grid-cols-1 grid-rows-[1fr] bg-linear-to-b from-[#00819c] from-60% to-cloverhound-blue-600 text-cloverhound-gray-900 dark:text-cloverhound-gray-50",
        sidebarOpen ? "lg:grid-cols-[72px_1fr]" : "lg:grid-cols-1",
      )}
    >
      {/* Backdrop for the mobile drawer. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Rail: fixed drawer below lg, static grid column at lg+. Hidden (and
          out of the grid) when collapsed on desktop. */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-[72px] transition-transform duration-regular ease-out-quad",
          "lg:static lg:w-auto lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:hidden",
        )}
      >
        <RailNav onClose={() => setSidebarOpen(false)} />
      </div>

      <div className="flex min-h-0 min-w-0 flex-col">
        <main
          className={cn(
            // The card: a header toolbar at the very top, then the scrolling
            // page content below a thin divider.
            "relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-cloverhound-gray-950",
            // Card shape (6px gradient gap on top/right/bottom, rounded
            // corners, shadow) only when the rail is docked at lg+. Collapsed —
            // or on mobile — the card is full-bleed: no margins, no rounding,
            // no shadow.
            sidebarOpen && "lg:mt-1.5 lg:mr-1.5 lg:mb-1.5 lg:rounded-lg lg:shadow-md",
          )}
        >
          {/* Card header: project switcher, plus a hamburger to reveal the
              rail — shown only while the rail is hidden (the rail has its own
              collapse control when docked). */}
          <header className="flex h-12 shrink-0 items-center gap-1.5 border-b border-cloverhound-gray-200 px-2 dark:border-cloverhound-gray-850">
            {!sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Show sidebar"
                className="flex size-8 shrink-0 items-center justify-center rounded-md text-cloverhound-gray-600 transition-colors hover:bg-cloverhound-gray-100 hover:text-cloverhound-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500 dark:text-cloverhound-gray-400 dark:hover:bg-cloverhound-gray-850 dark:hover:text-cloverhound-gray-50"
              >
                <MenuIcon className="size-5" aria-hidden="true" />
              </button>
            )}
            <ProjectSwitcher
              currentOrg={props.current_organization ?? null}
              currentProject={props.current_project ?? null}
              projects={props.projects ?? []}
            />
            <HeaderBreadcrumbs />
          </header>

          <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
            {children}
          </div>
        </main>
      </div>
      <Toaster />
    </div>
  )
}
